import React, { useEffect } from 'react';
import { X, BookMarked, ChevronLeft, ChevronRight } from 'lucide-react';
import { AuthorLinks, CategoryLink } from './CatalogFacets';
import { bookCollections, CollectionIcon } from './Collections';

const FIELDS = [
  ['titulo', 'Título'], ['subtitulo', 'Subtítulo'], ['autor', 'Autor(es)'], ['edicao', 'Edição'],
  ['ano_publicacao', 'Ano'], ['editora', 'Editora'], ['isbn', 'ISBN/ISSN'], ['categoria', 'Categoria'],
  ['paginas', 'Páginas'], ['idioma', 'Língua'], ['descricao', 'Descrição']
];

export const BookDetailsModal = ({ book, color, onClose, onRead, onFacet, collections = [] }) => {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const data = { ...book, paginas: book.paginas || book.page_count, idioma: book.idioma || 'Português' };
  const rows = FIELDS.filter(([k]) => data[k] !== undefined && data[k] !== null && String(data[k]).trim() !== '');
  const value = (k) => (k === 'autor' ? <AuthorLinks book={book} onFacet={onFacet} color={color} />
    : k === 'categoria' ? <CategoryLink book={book} onFacet={onFacet} color={color} /> : String(data[k]));
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="book-details-modal"
        style={{ background: 'white', borderRadius: 14, width: '100%', maxWidth: 560, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', borderTop: `4px solid ${color}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', borderBottom: '1px solid #e2e8f0' }}>
          <h3 style={{ fontWeight: 700, color: '#2D3748', margin: 0 }}>Dados do livro</h3>
          <button onClick={onClose} data-testid="book-details-close" title="Fechar" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#718096' }}><X className="w-5 h-5" /></button>
        </div>
        <dl style={{ overflowY: 'auto', padding: '1rem 1.25rem', margin: 0, textAlign: 'left' }}>
          {rows.map(([k, label]) => (
            <div key={k} style={{ marginBottom: '0.85rem' }} data-testid={`book-details-${k}`}>
              <dt style={{ fontSize: '0.7rem', color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</dt>
              <dd style={{ margin: 0, color: '#2D3748', fontWeight: k === 'titulo' ? 600 : 400, whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.5 }}>{value(k)}</dd>
            </div>
          ))}
          <div style={{ marginBottom: '0.85rem' }} data-testid="book-details-colecoes">
            <dt style={{ fontSize: '0.7rem', color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Coleções</dt>
            <dd style={{ margin: '4px 0 0', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {bookCollections(book, collections).map((c) => (
                <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '0.2rem 0.65rem', borderRadius: 999, fontSize: '0.82rem', background: `color-mix(in srgb, ${color} 12%, white)`, color: '#2D3748' }}>
                  <CollectionIcon name={c.icon} className="w-3.5 h-3.5" />{c.nome}
                </span>
              ))}
            </dd>
          </div>
        </dl>
        <div style={{ padding: '0.9rem 1.25rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onRead} data-testid="book-details-read-btn"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: color, color: 'white', border: 'none', borderRadius: 8, padding: '0.6rem 1.2rem', fontWeight: 600, cursor: 'pointer' }}>
            <BookMarked className="w-4 h-4" /> Ler Agora
          </button>
        </div>
      </div>
    </div>
  );
};

export const CatalogPagination = ({ page, totalPages, onChange, color }) => {
  if (totalPages <= 1) return null;
  const btn = (active, disabled) => ({
    minWidth: 38, height: 38, borderRadius: 8, border: `1px solid ${active ? color : '#e2e8f0'}`, cursor: disabled ? 'not-allowed' : 'pointer',
    background: active ? color : 'white', color: active ? 'white' : '#4a5568', fontWeight: 600, opacity: disabled ? 0.4 : 1,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'background-color 0.2s ease'
  });
  return (
    <nav data-testid="catalog-pagination" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.4rem', marginTop: '2rem', flexWrap: 'wrap' }}>
      <button style={btn(false, page === 1)} disabled={page === 1} onClick={() => onChange(page - 1)} data-testid="catalog-page-prev" title="Página anterior"><ChevronLeft className="w-4 h-4" /></button>
      {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
        <button key={n} style={btn(n === page, false)} onClick={() => onChange(n)} data-testid={`catalog-page-${n}`}>{n}</button>
      ))}
      <button style={btn(false, page === totalPages)} disabled={page === totalPages} onClick={() => onChange(page + 1)} data-testid="catalog-page-next" title="Próxima página"><ChevronRight className="w-4 h-4" /></button>
    </nav>
  );
};
